// src/components/MarshallAreaStandalone.js - Updated for Combined Fixed Header

import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { getDancersList, normalizeDancer } from '../utils/fieldCompatibility';
import './MarshallArea.css';

const _host = 'chappie-demo.novosense.africa';
const _company = 'wdsf';

function MarshallAreaStandalone() {
  const { competitionId } = useParams();
  const [selectedCompetition, setSelectedCompetition] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showDancersModal, setShowDancersModal] = useState(false);
  const [selectedEventForDancers, setSelectedEventForDancers] = useState(null);
  const [showCheckInConfirmation, setShowCheckInConfirmation] = useState(false);
  const [selectedDancerForCheckIn, setSelectedDancerForCheckIn] = useState(null);
  const [checkedInDancers, setCheckedInDancers] = useState(new Set());
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  // Manual cloth number lookup state
  const [showClothNumberModal, setShowClothNumberModal] = useState(false);
  const [clothNumberInput, setClothNumberInput] = useState('');
  const [foundDancer, setFoundDancer] = useState(null);
  const [foundDancerEvent, setFoundDancerEvent] = useState(null);
  const [isSearching, setIsSearching] = useState(false);

  // Club data for location lookup
  const [clubs, setClubs] = useState([]);

  // Detect screen size changes
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Fetch competition details
  const fetchCompetition = async () => {
    try {
      const response = await fetch(`https://${_host}:6003/api/${_company}/competitions/${competitionId}`);
      if (response.ok) {
        const competition = await response.json();
        setSelectedCompetition(competition);
      }
    } catch (err) {
      console.error('Error fetching competition:', err);
    }
  };

  // Fetch couples data to get check-in status
  const fetchCouplesWithCheckInStatus = async () => {
    try {
      const response = await fetch(`https://${_host}:6003/api/${_company}/couples`);
      if (!response.ok) {
        throw new Error('Failed to fetch couples');
      }
      return await response.json();
    } catch (err) {
      console.error('Error fetching couples:', err);
      return [];
    }
  };

  // Fetch clubs data for location lookup
  const fetchClubs = async () => {
    try {
      const response = await fetch(`https://${_host}:6003/api/${_company}/clubs`);
      if (response.ok) {
        const clubsData = await response.json();
        console.log('Marshall Area - Loaded clubs:', clubsData);
        setClubs(clubsData);
        return clubsData;
      }
      return [];
    } catch (error) {
      console.error('Error fetching clubs:', error);
      return [];
    }
  };

  // Cache for dancer location lookup - stores final location strings
  const [dancerLocationCache, setDancerLocationCache] = useState(new Map());

  // Get dancer location based on club - simple, synchronous lookup
  const getDancerLocation = (dancer) => {
    // Check cache first
    if (dancerLocationCache.has(dancer.coupleId)) {
      return dancerLocationCache.get(dancer.coupleId);
    }

    // Fallback to dancer country if not cached yet
    return dancer.country || 'Unknown';
  };

  // Build dancer location cache from all couples data (called once)
  const buildDancerLocationCache = async () => {
    if (clubs.length === 0) return;

    try {
      console.log('Building dancer location cache from all couples...');
      const response = await fetch(`https://${_host}:6003/api/${_company}/couples`);
      if (response.ok) {
        const allCouples = await response.json();
        console.log('Loaded', allCouples.length, 'couples for location cache');

        const newCache = new Map();

        allCouples.forEach(couple => {
          let location = couple.country || 'Unknown';

          // Try to find club by clubId
          let club = null;
          if (couple.clubId) {
            club = clubs.find(c => c.id === couple.clubId);
          }

          // Try to find club by clubName
          if (!club && couple.clubName) {
            club = clubs.find(c => c.name.toLowerCase() === couple.clubName.toLowerCase());
          }

          // If club found with province and country, use that
          if (club && club.province && club.country) {
            location = `${club.province}, ${club.country}`;
          } else if (club && club.country) {
            location = club.country;
          }

          // Cache the result
          newCache.set(couple.id, location);
        });

        console.log('Built location cache with', newCache.size, 'entries');
        setDancerLocationCache(newCache);
      }
    } catch (error) {
      console.error('Error building dancer location cache:', error);
    }
  };

  // Fetch events for the specific competition
  const fetchEvents = async () => {
    try {
      const [eventsResponse, couplesData, clubsData] = await Promise.all([
        fetch(`https://${_host}:6003/api/${_company}/events`),
        fetchCouplesWithCheckInStatus(),
        fetchClubs()
      ]);

      if (!eventsResponse.ok) {
        throw new Error('Failed to fetch events');
      }

      const eventsData = await eventsResponse.json();

      // Filter events by competition ID from URL
      const filteredEvents = eventsData.filter(event =>
        (event.competitionId === competitionId) || (event.competition_id === competitionId)
      );

      // Remove duplicate events by ID (extra safety)
      const uniqueEvents = filteredEvents.filter((event, index, self) =>
        index === self.findIndex(e => e.id === event.id)
      );

      // Enrich dancer data with check-in status from couples data
      const enrichedEvents = uniqueEvents.map(event => {
        const dancers = getDancersList(event);
        if (dancers && dancers.length > 0) {
          event.dancers = dancers.map(dancer => {
            // Find matching couple data to get check-in status
            const coupleData = couplesData.find(couple => couple.id === dancer.coupleId);
            if (coupleData) {
              return {
                ...dancer,
                marshallCheckin: coupleData.marshallCheckin || false,
                marshallCheckinTime: coupleData.marshallCheckinTime || null
              };
            }
            return dancer;
          });
        }
        return event;
      });

      // Prevent duplicate events by checking if events have actually changed
      setEvents(prevEvents => {
        // Simple check - if length is different, definitely update
        if (prevEvents.length !== enrichedEvents.length) {
          return enrichedEvents;
        }

        // Check if events are actually different (by comparing IDs)
        const prevIds = prevEvents.map(e => e.id).sort();
        const newIds = enrichedEvents.map(e => e.id).sort();

        if (JSON.stringify(prevIds) !== JSON.stringify(newIds)) {
          return enrichedEvents;
        }

        // Events are the same, return previous state to prevent re-renders
        return prevEvents;
      });
      setLoading(false);
    } catch (err) {
      console.error('Error fetching events:', err);
      setError('Failed to load events. Please try again later.');
      setLoading(false);
    }
  };

  useEffect(() => {
    if (competitionId) {
      fetchCompetition();

      // Build location cache after initial data load
      const timer = setTimeout(() => {
        buildDancerLocationCache();
        fetchEvents(); // Initial fetch after delay to avoid race conditions
      }, 1000); // Wait 1 second before starting

      const interval = setInterval(fetchEvents, 5000);
      return () => {
        clearInterval(interval);
        clearTimeout(timer);
      };
    }
  }, [competitionId]);

  // Sort events by item number
  const sortedEvents = [...events].sort((a, b) => {
    const itemA = a.itemNumber || 999;
    const itemB = b.itemNumber || 999;
    return itemA - itemB;
  });

  // Filter events based on search query
  const filteredEvents = sortedEvents.filter(event => {
    if (!searchQuery.trim()) return true;

    const query = searchQuery.toLowerCase().trim();

    // Search by item number
    const itemNumber = (event.itemNumber || sortedEvents.indexOf(event) + 1).toString();
    if (itemNumber.includes(query)) return true;

    // Search by event name
    if (event.name && event.name.toLowerCase().includes(query)) return true;

    // Search by round type
    if (event.roundType && event.roundType.toLowerCase().includes(query)) return true;

    return false;
  });

  const handleItemNumberClick = (event, e) => {
    e.stopPropagation();
    setSelectedEventForDancers(event);
    setShowDancersModal(true);
  };

  const handleCloseModal = () => {
    setShowDancersModal(false);
    setSelectedEventForDancers(null);
  };

  const checkDancerRegistrationStatus = async (clothNumber) => {
    try {
      const response = await fetch(`https://${_host}:6003/api/${_company}/couples/by-cloth-number/${clothNumber}`);
      if (!response.ok) {
        return { canCheckIn: false, error: true };
      }

      const dancerData = await response.json();
      const registrationCheckin = dancerData?.registration_checkin || dancerData?.registrationCheckin;

      // Allow check-in only if registrationCheckin is explicitly true
      return {
        canCheckIn: registrationCheckin === true,
        dancerData: dancerData,
        error: false
      };
    } catch (error) {
      return { canCheckIn: false, error: true };
    }
  };

  const handleDancerClick = async (dancer, e) => {
    e.stopPropagation();

    const clothNumber = dancer?.clothNumber || dancer?.cloth_number || dancer?.number;
    console.log('*** CHECKING DANCER WITH CLOTH NUMBER:', clothNumber, '***');

    // HARD TEST: Always block dancer 800 for debugging - COMMENTED OUT FOR REAL TESTING
    // if (String(clothNumber).trim() === '800') {
    //   console.log('*** HARD TEST BLOCK TRIGGERED - BLOCKING DANCER 800 ***');
    //   alert('TEST BLOCK: Dancer 800 cannot be checked in - registration check-in not completed');
    //   return;
    // }

    // Check registration status from database
    const statusCheck = await checkDancerRegistrationStatus(clothNumber);

    if (statusCheck.error) {
      alert('Error: Unable to verify dancer registration status. Please try again.');
      return;
    }

    if (!statusCheck.canCheckIn) {
      alert(`Cannot check in dancer ${clothNumber}: Registration check-in not completed. Please complete administration check-in first.`);
      return;
    }

    // If we get here, registration check-in passed
    console.log('*** REGISTRATION CHECK-IN PASSED - ALLOWING CHECK-IN ***');
    setSelectedDancerForCheckIn(statusCheck.dancerData);
    setShowCheckInConfirmation(true);
  };

  const handleConfirmCheckIn = async () => {
    if (!selectedDancerForCheckIn || !selectedEventForDancers) return;

    try {
      // Create a unique identifier for this dancer in this event
      const dancerId = `${selectedEventForDancers.id}-${selectedDancerForCheckIn.id}`;

      // Here you would implement the actual check-in logic
      console.log(`Checking in dancer ${selectedDancerForCheckIn.name} (${selectedDancerForCheckIn.clothNumber}) for Marshall`);

      // Use multiple ID fallbacks to ensure we have a valid ID for the API call
      const apiId = selectedDancerForCheckIn.id ||
                     selectedDancerForCheckIn._original?.id ||
                     selectedDancerForCheckIn._original?.couple_id ||
                     selectedDancerForCheckIn._original?.coupleId ||
                     selectedDancerForCheckIn._original?.rawDancer?.coupleId; // Fallback to raw data

      console.log('Standalone API call - using ID:', apiId, 'from dancer:', selectedDancerForCheckIn);
      console.log('Raw dancer _original:', selectedDancerForCheckIn._original);

      // If we still don't have a valid ID, we need to get it from somewhere else
      // Try to get the raw dancer from the selected event as emergency fallback
      let finalApiId = apiId ||
                       selectedDancerForCheckIn._original?.rawDancer?.coupleId ||
                       selectedDancerForCheckIn._original?.coupleId;

      // Emergency fallback: find the dancer in the event by cloth number and use their coupleId
      if (!finalApiId && selectedEventForDancers?.dancers) {
        const emergencyDancer = selectedEventForDancers.dancers.find(d =>
          (d.clothNumber === selectedDancerForCheckIn.clothNumber) ||
          (d.cloth_number === selectedDancerForCheckIn.clothNumber)
        );
        if (emergencyDancer?.coupleId) {
          finalApiId = emergencyDancer.coupleId;
          console.log('Emergency fallback found dancer ID:', finalApiId, 'from emergency dancer:', emergencyDancer);
        }
      }

      console.log('Final API ID being used:', finalApiId);

      // Safety check: if we still don't have a valid ID, abort the check-in
      if (!finalApiId) {
        console.error('No valid dancer ID found for API call:', selectedDancerForCheckIn);
        alert('Error: Unable to identify dancer for check-in. Please try again.');
        setShowCheckInConfirmation(false);
        setSelectedDancerForCheckIn(null);
        return;
      }

      // Update dancer's Marshall check-in status using the couples API
      const response = await fetch(`https://${_host}:6003/api/${_company}/couples/${finalApiId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          marshallCheckin: true,
          marshallCheckinTime: new Date().toISOString()
        })
      });

      if (response.ok) {
        // Mark dancer as checked in in local state for immediate UI update
        setCheckedInDancers(prev => new Set(prev).add(dancerId));

        // Show success message
        const successMessage = document.createElement('div');
        successMessage.className = 'check-in-success-message';
        successMessage.textContent = `✓ ${selectedDancerForCheckIn.name} (${selectedDancerForCheckIn.clothNumber}) checked in successfully!`;
        successMessage.style.cssText = `
          position: fixed;
          top: 20px;
          right: 20px;
          background: linear-gradient(135deg, #4caf50 0%, #45a049 100%);
          color: white;
          padding: 15px 20px;
          border-radius: 8px;
          box-shadow: 0 4px 12px rgba(76, 175, 80, 0.3);
          z-index: 2000;
          font-weight: 600;
          animation: slideInRight 0.3s ease-out;
          ${isMobile ? 'left: 20px; right: 20px; top: 10px;' : ''}
        `;
        document.body.appendChild(successMessage);

        // Remove success message after 3 seconds
        setTimeout(() => {
          if (document.body.contains(successMessage)) {
            document.body.removeChild(successMessage);
          }
        }, 3000);

        // Refresh events to get updated dancer data from server
        // This ensures the marshallCheckin property is properly updated
        await fetchEvents();
      }

      setShowCheckInConfirmation(false);
      setSelectedDancerForCheckIn(null);
    } catch (error) {
      console.error('Error checking in dancer:', error);
      alert('Failed to check in dancer. Please try again.');
      setShowCheckInConfirmation(false);
      setSelectedDancerForCheckIn(null);
    }
  };

  const handleCancelCheckIn = () => {
    setShowCheckInConfirmation(false);
    setSelectedDancerForCheckIn(null);
  };

  // Manual cloth number lookup handlers
  const handleClothNumberSearch = async () => {
    const trimmedClothNumber = clothNumberInput.trim();
    if (!trimmedClothNumber) {
      alert('Please enter a cloth number');
      return;
    }

    setIsSearching(true);
    setFoundDancer(null);
    setFoundDancerEvent(null);

    try {
      // Search through all events and dancers for matching cloth number
      for (const event of events) {
        const dancers = getDancersList(event);
        if (dancers && dancers.length > 0) {
          const dancer = dancers.find(d =>
            d.clothNumber && d.clothNumber.toString() === trimmedClothNumber
          );

          if (dancer) {
            setFoundDancer(dancer);
            setFoundDancerEvent(event);
            break;
          }
        }
      }

      setIsSearching(false);
    } catch (error) {
      console.error('Error searching for cloth number:', error);
      alert('Error searching for dancer. Please try again.');
      setIsSearching(false);
    }
  };

  const handleClothNumberCheckIn = async () => {
    if (foundDancer && foundDancerEvent) {
      // Check registration status before allowing check-in
      const clothNumber = foundDancer?.clothNumber || foundDancer?.cloth_number || foundDancer?.number;
      console.log(`TESTING: Quick Check-in registration check for dancer ${clothNumber}`);

      const statusCheck = await checkDancerRegistrationStatus(clothNumber);
      console.log('TESTING: Quick Check-in registration status:', JSON.stringify(statusCheck, null, 2));

      if (statusCheck.error) {
        alert('Error: Unable to verify dancer registration status. Please try again.');
        return;
      }

      if (!statusCheck.canCheckIn) {
        alert(`Cannot check in dancer ${clothNumber}: Registration check-in not completed. Please complete administration check-in first.`);
        return;
      }

      // If registration check passes, proceed with normal check-in flow
      setSelectedDancerForCheckIn(statusCheck.dancerData);
      setSelectedEventForDancers(foundDancerEvent);
      setShowClothNumberModal(false);
      setShowCheckInConfirmation(true);
    }
  };

  const handleClothNumberModalClose = () => {
    setShowClothNumberModal(false);
    setClothNumberInput('');
    setFoundDancer(null);
    setFoundDancerEvent(null);
    setIsSearching(false);
  };

  return (
    <div className="marshall-area-container">
      {/* Combined Fixed Header */}
      <div className="fixed-header">
        <div className="page-header">
          <h1>Marshall Area</h1>
          {selectedCompetition && (
            <p className="page-subtitle">{selectedCompetition.name}</p>
          )}
        </div>
        
        <div className="events-table-header">
          <div className="header-actions">
            <button
              className="cloth-number-btn"
              onClick={() => setShowClothNumberModal(true)}
              title="Check in dancer by cloth number"
            >
              <span className="cloth-number-icon">🔍</span>
              <span className="cloth-number-text">Quick Check-in</span>
            </button>
            <div className="search-container">
              <input
                type="text"
                placeholder="Search by item number, event name, or round type..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="search-input"
              />
              {searchQuery && (
                <button
                  className="search-clear-btn"
                  onClick={() => setSearchQuery('')}
                  title="Clear search"
                >
                  ×
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {error && <div className="error-banner">{error}</div>}

      {loading ? (
        <div className="loading-message">Loading events...</div>
      ) : events.length === 0 ? (
        <div className="empty-message">
          <p>No events available.</p>
          <p className="hint">Events must be created in Administration first.</p>
        </div>
      ) : (
        <div className="marshall-area-content">
          <div className="events-table-container">
            <div className="events-table-scrollable">
              <table className="events-table">
                <thead>
                  <tr>
                    <th>Item #</th>
                    <th>Event Name</th>
                    <th>Round Type</th>
                    <th>Heats</th>
                    <th>Dancers</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredEvents.map((event) => (
                    <tr key={event.id}>
                      <td data-label="Item">
                        <span
                          className="item-number-badge clickable"
                          onClick={(e) => handleItemNumberClick(event, e)}
                          title="Click to view dancers"
                        >
                          {event.itemNumber || '-'}
                        </span>
                      </td>
                      <td data-label="Event">
                        <div className="event-name-cell">
                          <div className="event-name">{event.name}</div>
                          <div className="event-meta">
                            {event.dances?.length || 0} dances
                          </div>
                        </div>
                      </td>
                      <td data-label="Round">
                        <span className={`round-badge ${event.roundType === 'TBD' ? 'tbd' : ''}`}>
                          {event.roundType || 'TBD'}
                        </span>
                      </td>
                      <td data-label="Heats">
                        <span className="heats-display">{event.numHeats || 1}</span>
                      </td>
                      <td data-label="Dancers">
                        <span className="dancers-count">
                          {getDancersList(event)?.length || 0}
                        </span>
                      </td>
                      <td data-label="Status">
                        <span className={`status-badge ${event.broadcast_state === 'live' ? 'ready' : 'pending'}`}>
                          {event.broadcast_state === 'live' ? 'Ready' : 'Pending'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {filteredEvents.length === 0 && searchQuery && (
                <div className="no-search-results">
                  <p>No events found matching "{searchQuery}"</p>
                  <button
                    className="clear-search-btn"
                    onClick={() => setSearchQuery('')}
                  >
                    Clear search
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Dancers Modal */}
      {showDancersModal && selectedEventForDancers && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className={`modal-content ${isMobile ? 'mobile-modal' : ''}`} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Dancers in Event</h3>
              <button className="modal-close-btn" onClick={handleCloseModal}>×</button>
            </div>
            <div className="modal-event-info">
              <span className="modal-item-number">{selectedEventForDancers.itemNumber}</span>
              <span className="modal-event-name">{selectedEventForDancers.name}</span>
              <span className="modal-round-type">{selectedEventForDancers.roundType || 'TBD'}</span>
            </div>
            <div className="modal-body">
              {selectedEventForDancers.dancers && selectedEventForDancers.dancers.length > 0 ? (
                <>
                  <div className="dancers-header">
                    <span className="dancers-count-header">
                      {selectedEventForDancers.dancers.length} Dancer{selectedEventForDancers.dancers.length !== 1 ? 's' : ''}
                    </span>
                  </div>
                  <div className="modal-dancers-list">
                    {selectedEventForDancers.dancers.map((dancer, index) => {
                      // Debug: Log the raw dancer data to see what fields are available
                      console.log('Raw dancer data:', dancer);

                      // Debug: Check what normalizeDancer returns for this specific dancer
                      console.log('About to normalize dancer with coupleId:', dancer.coupleId);
                      console.log('Raw dancer registrationCheckin:', dancer.registrationCheckin);
                      console.log('Raw dancer registration_checkin:', dancer.registration_checkin);
                      console.log('Raw dancer marshallCheckin:', dancer.marshallCheckin);
                      console.log('Raw dancer marshall_checkin:', dancer.marshall_checkin);

                      const normalizedDancer = normalizeDancer(dancer);
                      console.log('Normalization returned ID:', normalizedDancer?.id);
                      console.log('Normalized registrationCheckin:', normalizedDancer.registrationCheckin);
                      console.log('Normalized marshallCheckin:', normalizedDancer.marshallCheckin);
                      console.log('Full normalized dancer object:', normalizedDancer);
                      console.log('DEBUG: Check normalizeDancer field mapping:');
                      console.log('- dancer.coupleId:', dancer.coupleId);
                      console.log('- dancer.couple_id:', dancer.couple_id);
                      console.log('- dancer.id:', dancer.id);
                      console.log('- Expected normalized.id to be coupleId if available');

                      // Explicitly check what type and value we have for registrationCheckin
                      console.log('=== VALIDATION DEBUG ===');
                      console.log('dancer.registrationCheckin === true:', dancer.registrationCheckin === true);
                      console.log('dancer.registrationCheckin === false:', dancer.registrationCheckin === false);
                      console.log('normalizedDancer.registrationCheckin === true:', normalizedDancer.registrationCheckin === true);
                      console.log('normalizedDancer.registrationCheckin === false:', normalizedDancer.registrationCheckin === false);
                      console.log('typeof normalizedDancer.registrationCheckin:', typeof normalizedDancer.registrationCheckin);

                      // Include raw dancer data for emergency fallbacks
                      normalizedDancer._original = { ...dancer, rawDancer: dancer };

                      console.log('Normalized dancer data:', normalizedDancer);
                      console.log('Raw dancer coupleId:', dancer.coupleId);
                      console.log('Raw dancer id:', dancer.id);
                      console.log('Expected normalized ID:', normalizedDancer.id);

                      // Test if normalizeDancer is working correctly
                      if (!normalizedDancer.id && dancer.coupleId) {
                        console.error('CRITICAL: normalizeDancer failed to extract ID from dancer with coupleId:', dancer.coupleId);
                        // Manually set the ID as emergency fix
                        normalizedDancer.id = dancer.coupleId;
                        console.log('Manual ID fix applied:', normalizedDancer.id);
                      }

                      const dancerId = `${selectedEventForDancers.id}-${normalizedDancer.id}`;
                      // Check both local state and server data for check-in status
                      const isCheckedIn = checkedInDancers.has(dancerId) || normalizedDancer.marshallCheckin === true;

                      // Remove stale event data validation - we'll validate in real-time when clicked
                      // Don't show warning until we actually check registration status
                      const canCheckIn = true; // Assume true for UI, validate on click

                      // Debug logging - remove this line in production
                      if (normalizedDancer.marshallCheckin) {
                        console.log(`Dancer ${normalizedDancer.name} is checked in according to server data`);
                      }

                      return (
                        <div
                          key={index}
                          className={`modal-dancer-item ${isCheckedIn ? 'checked-in' : ''}`}
                          onClick={(e) => handleDancerClick(normalizedDancer, e)}
                          title={
                            isCheckedIn ? "Checked in for Marshall" : "Click to check in dancer"
                          }
                        >
                          <div className="modal-dancer-number">{normalizedDancer.clothNumber}</div>
                          <div className="modal-dancer-info">
                            <div className="modal-dancer-name">{normalizedDancer.name}</div>
                            <div className="modal-dancer-details">
                              <span className="modal-dancer-type">{normalizedDancer.dancerType || 'Solo'}</span>
                              {getDancerLocation(normalizedDancer) && (
                                <span className="modal-dancer-country">• {getDancerLocation(normalizedDancer)}</span>
                              )}
                            </div>
                          </div>
                          <div className="check-in-indicator">
                            <span className="check-in-icon">{isCheckedIn ? '✓' : ''}</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </>
              ) : (
                <div className="modal-empty-message">
                  <p>No dancers allocated to this event</p>
                  <span className="modal-hint">Dancers need to be allocated from the Administration section</span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Check-in Confirmation Modal */}
      {showCheckInConfirmation && selectedDancerForCheckIn && (
        <div className="modal-overlay check-in-overlay" onClick={handleCancelCheckIn}>
          <div className="check-in-modal" onClick={(e) => e.stopPropagation()}>
            <div className="check-in-header">
              <div className="check-in-icon-large">👤</div>
              <h3>Confirm Check-in</h3>
              <button className="check-in-close-btn" onClick={handleCancelCheckIn}>×</button>
            </div>

            <div className="check-in-content">
              <div className="check-in-dancer-info">
                <div className="check-in-dancer-number">{selectedDancerForCheckIn.clothNumber}</div>
                <div className="check-in-dancer-details">
                  <div className="check-in-dancer-name">{selectedDancerForCheckIn.name}</div>
                  <div className="check-in-dancer-meta">
                    <span className="check-in-dancer-type">{selectedDancerForCheckIn.type || 'Solo'}</span>
                    {getDancerLocation(selectedDancerForCheckIn) && (
                      <span className="check-in-dancer-country">• {getDancerLocation(selectedDancerForCheckIn)}</span>
                    )}
                  </div>
                </div>
              </div>

              <div className="check-in-event-info">
                <strong>Event:</strong> {selectedEventForDancers.name} (#{selectedEventForDancers.itemNumber})
              </div>

              <div className="check-in-confirmation">
                <p>Are you sure you want to check in this dancer for Marshall?</p>
                <div className="check-in-actions">
                  <button className="check-in-cancel-btn" onClick={handleCancelCheckIn}>
                    Cancel
                  </button>
                  <button className="check-in-confirm-btn" onClick={handleConfirmCheckIn}>
                    ✓ Confirm Check-in
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Cloth Number Lookup Modal */}
      {showClothNumberModal && (
        <div className="modal-overlay check-in-overlay" onClick={handleClothNumberModalClose}>
          <div className="check-in-modal cloth-number-modal" onClick={(e) => e.stopPropagation()}>
            <div className="check-in-header">
              <div className="check-in-icon-large">🔍</div>
              <h3>Quick Check-in by Cloth Number</h3>
              <button className="check-in-close-btn" onClick={handleClothNumberModalClose}>×</button>
            </div>

            <div className="check-in-content">
              <div className="cloth-number-input-section">
                <label className="input-label">Enter Cloth Number:</label>
                <div className="cloth-input-container">
                  <input
                    type="tel"
                    value={clothNumberInput}
                    onChange={(e) => {
                      // Only allow numbers
                      const value = e.target.value.replace(/[^0-9]/g, '');
                      setClothNumberInput(value);
                    }}
                    placeholder="Enter cloth number..."
                    className="cloth-number-input-field"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !isSearching) {
                        handleClothNumberSearch();
                      }
                    }}
                  />
                  <button
                    className="search-cloth-btn"
                    onClick={handleClothNumberSearch}
                    disabled={isSearching}
                  >
                    {isSearching ? 'Searching...' : 'Search'}
                  </button>
                </div>
              </div>

              {foundDancer && foundDancerEvent ? (
                <div className="found-dancer-section">
                  <div className="found-dancer-info">
                    <div className="found-dancer-number">{foundDancer.clothNumber}</div>
                    <div className="found-dancer-details">
                      <div className="found-dancer-name">{foundDancer.name}</div>
                      <div className="found-dancer-meta">
                        <span className="found-dancer-type">{foundDancer.type || 'Solo'}</span>
                        {getDancerLocation(foundDancer) && (
                          <span className="found-dancer-country">• {getDancerLocation(foundDancer)}</span>
                        )}
                      </div>
                      <div className="found-dancer-event">
                        <strong>Event:</strong> {foundDancerEvent.name} (#{foundDancerEvent.itemNumber})
                      </div>
                    </div>
                  </div>

                  <div className="found-dancer-actions">
                    <button className="check-in-cancel-btn" onClick={handleClothNumberModalClose}>
                      Cancel
                    </button>
                    <button className="check-in-confirm-btn" onClick={handleClothNumberCheckIn}>
                      ✓ Check In Dancer
                    </button>
                  </div>
                </div>
              ) : !isSearching && clothNumberInput.trim() ? (
                <div className="no-dancer-found">
                  <p>No dancer found with cloth number "{clothNumberInput.trim()}"</p>
                  <button className="check-in-cancel-btn" onClick={() => setClothNumberInput('')}>
                      Clear and Try Again
                    </button>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default MarshallAreaStandalone;